import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Video, VideoType } from '../types';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { DataApi } from '@utils/data';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { REFRESH_INTERVAL, NEW_VIDEO_STYLE_TIMEOUT, SEARCH_DEBOUNCE_TIME } from '../constants';

export const useVideos = (videoLoader: DataApi<Video> | undefined) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [isListVisible, setIsListVisible] = useState(true);
  const [videoIndex, setVideoIndex] = useState(0);
  const [videoType, setVideoType] = useState<VideoType>(VideoType.ALL);
  const selectedVideoRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useScreenSize();
  const [isWatchingVideo, setIsWatchingVideo] = useState(false);
  const [newVideoIds, setNewVideoIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const scrollToSelectedVideo = useCallback(() => {
    if (selectedVideoRef.current && isListVisible) {
      setTimeout(
        () => {
          selectedVideoRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: isMobile ? 'center' : 'start',
          });
        },
        isMobile ? 300 : 0
      );
    }
  }, [isListVisible, isMobile]);

  const handleVideoClick = (videoId: string) => {
    onVideoClick(videoId);
    if (isMobile) {
      toggleVideoList();
    }
  };

  const hasMoreVideos = useMemo(() => {
    return videoLoader?.hasMoreData();
  }, [videos]);

  const fetchVideos = useCallback(async () => {
    try {
      setIsLoading(true);
      const newVideos = await videoLoader?.loadData();
      if (newVideos) {
        setVideos(newVideos);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error fetching videos');
    } finally {
      setIsLoading(false);
    }
  }, [videoLoader, selectedVideoId]);

  const fetchShorts = useCallback(async () => {
    try {
      setIsLoading(true);
      if (videoLoader) {
        const newVideos = await videoLoader.loadData();
        setVideos(newVideos.filter(v => v.type === VideoType.SHORTS));
      }
    } catch (e) {
      console.error(e);
      toast.error('Error fetching shorts');
    } finally {
      setIsLoading(false);
    }
  }, [videoLoader]);

  const refreshVideos = useCallback(async () => {
    if (!selectedVideoId && !isWatchingVideo && videoLoader) {
      const newVideos = await videoLoader?.simpleLoadData();
      const currentVideoIds = videos.map(v => v.id);

      const addedVideoIds = newVideos.filter(v => !currentVideoIds.includes(v.id)).map(v => v.id);

      if (addedVideoIds.length > 0) {
        setNewVideoIds(new Set(addedVideoIds));

        setTimeout(() => {
          setNewVideoIds(new Set());
        }, NEW_VIDEO_STYLE_TIMEOUT);
      }
    }
  }, [selectedVideoId, videoLoader]);

  useEffect(() => {
    const refreshVideosInterval = setInterval(() => {
      refreshVideos();
    }, REFRESH_INTERVAL);
    return () => clearInterval(refreshVideosInterval);
  }, [videoLoader, selectedVideoId, isWatchingVideo]);

  useEffect(() => {
    if (!selectedVideoId) {
      setIsWatchingVideo(false);
    }
  }, [selectedVideoId]);

  const onVideoClick = (videoId: string) => {
    setSelectedVideoId(videoId);
    setVideoIndex(videos.findIndex(v => v.id === videoId));
    setIsWatchingVideo(true);
    setTimeout(scrollToSelectedVideo, 0);
  };

  const toggleVideoList = () => {
    setIsListVisible(!isListVisible);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        try {
          setIsLoading(true);
          if (videoLoader) {
            videoLoader.addSearchFilter(searchTerm);

            const newVideos = await videoLoader?.loadData();
            if (newVideos) {
              const videoStillExists = newVideos.some(v => v.id === selectedVideoId);
              setVideos(newVideos);
              if (!videoStillExists) {
                // setSelectedVideoId(null);
              }
            }
          }
        } catch (e) {
          console.error(e);
          toast.error('Error fetching videos');
        } finally {
          setIsLoading(false);
        }
      }, SEARCH_DEBOUNCE_TIME),
    [videoLoader, selectedVideoId]
  );

  const onSearch = useCallback(
    (searchTerm: string) => {
      debouncedSearch(searchTerm);
    },
    [debouncedSearch]
  );

  const onVideoTypeChange = useCallback(
    async (newType: VideoType) => {
      setVideoType(newType);
      if (videoLoader) {
        if (newType === VideoType.ALL) {
          const currentQuery = { ...videoLoader.query };
          delete currentQuery.video_type;
          videoLoader.resetQuery(currentQuery);
        } else {
          videoLoader.addFilter({ video_type: newType });
        }

        const newVideos = await videoLoader?.loadData();
        if (newVideos) {
          const videoStillExists = newVideos.some(v => v.id === selectedVideoId);
          setVideos(newVideos);
          if (!videoStillExists) {
            // setSelectedVideoId(null);
          } else {
            setTimeout(scrollToSelectedVideo, 0);
          }
        }
      }
    },
    [videoLoader, selectedVideoId, scrollToSelectedVideo]
  );

  const onNextVideo = () => {
    setSelectedVideoId(videos[videoIndex + 1].id);
    setVideoIndex(videoIndex + 1);
  };

  const onPreviousVideo = () => {
    setSelectedVideoId(videos[videoIndex - 1].id);
    setVideoIndex(videoIndex - 1);
  };

  const loadMoreVideos = async () => {
    if (videoLoader && hasMoreVideos) {
      try {
        const videos = await videoLoader?.loadData();
        if (videos) {
          setVideos(prev => [...prev, ...videos]);
        }
      } catch (e) {
        console.error(e);
        toast.error('Error fetching videos');
      }
    }
  };

  useEffect(() => {
    if (isListVisible && selectedVideoId && selectedVideoRef.current) {
      scrollToSelectedVideo();
    }
  }, [isListVisible, selectedVideoId, scrollToSelectedVideo]);

  return {
    videos,
    selectedVideoId,
    isListVisible,
    videoIndex,
    fetchVideos,
    toggleVideoList,
    loadMoreVideos,
    hasMoreVideos,
    onSearch,
    onVideoTypeChange,
    videoType,
    onNextVideo,
    onPreviousVideo,
    handleVideoClick,
    selectedVideoRef,
    videoLoader,
    newVideoIds,
    fetchShorts,
    isLoading,
  };
};
